import { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import { cerrarSesionLocal } from '@/shared/api/authBridge';
import { authApi, type RegistroInput } from '@/shared/api/services/authApi';
import { usuariosApi, type PatchUsuario } from '@/shared/api/services/usuariosApi';
import type { ApiResult } from '@/shared/api/types';
import type { Usuario } from '@/shared/domain/types';
import type { AppStoreLike } from '@/shared/store/listeners/types';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { setSession, setUser } from '@/shared/store/slices/authSlice';

export interface UseAuth {
  user: Usuario | null;
  status: 'anon' | 'auth';
  isAuthenticated: boolean;
  /** Hay un usuario recordado pero la sesión aún no se renovó (arranque). */
  isRestoring: boolean;
  login: (email: string, password: string) => Promise<ApiResult<Usuario>>;
  registro: (input: RegistroInput) => Promise<ApiResult<Usuario>>;
  /** Cierra la sesión en el servidor (si se puede) y siempre en este dispositivo. */
  logout: () => Promise<void>;
  logoutTodos: () => Promise<ApiResult<void>>;
  actualizarPerfil: (cambios: PatchUsuario) => Promise<ApiResult<Usuario>>;
}

/** Sesión de la cuenta. Todas las acciones devuelven ApiResult: la pantalla decide cómo mostrar el error. */
export function useAuth(): UseAuth {
  const dispatch = useAppDispatch();
  const store = useStore() as AppStoreLike;
  const user = useAppSelector((s) => s.auth.user);
  const status = useAppSelector((s) => s.auth.status);

  const abrirSesion = useCallback(
    (r: Awaited<ReturnType<typeof authApi.login>>): ApiResult<Usuario> => {
      if (!r.ok) return r;
      dispatch(setSession({ user: r.data.usuario, tokens: r.data.tokens }));
      return { ok: true, data: r.data.usuario };
    },
    [dispatch],
  );

  return useMemo<UseAuth>(
    () => ({
      user,
      status,
      isAuthenticated: status === 'auth',
      isRestoring: status === 'anon' && user !== null,
      login: async (email, password) => abrirSesion(await authApi.login(email, password)),
      registro: async (input) => abrirSesion(await authApi.registro(input)),
      logout: async () => {
        // El cierre local no espera al servidor ni depende de él.
        const refresh = store.getState().auth.refreshToken;
        const remoto = authApi.logout(refresh);
        cerrarSesionLocal(store, false);
        await remoto;
      },
      logoutTodos: async () => {
        const r = await authApi.logoutTodos();
        if (r.ok) cerrarSesionLocal(store, false);
        return r;
      },
      actualizarPerfil: async (cambios) => {
        const r = await usuariosApi.patchYo(cambios);
        if (r.ok) dispatch(setUser(r.data));
        return r;
      },
    }),
    [user, status, abrirSesion, store, dispatch],
  );
}
